'use client';

import { DarkModeProvider, useDarkMode } from '@/components/DarkModeProvider';
import { AuthProvider } from '@/contexts/AuthContext';
import { ToastProvider } from '@/contexts/ToastContext';
import { NotificationProvider } from '@/contexts/NotificationContext';
import { ReactNode } from 'react';
import ErrorBoundary from '@/components/ErrorBoundary';
import PWAInstallPrompt from '@/components/PWAInstallPrompt';
import Header from '@/components/Header';
import AuthGuard from '@/components/AuthGuard';
import { usePathname } from 'next/navigation';

// Routes that do NOT require authentication
const PUBLIC_ROUTES = [
  '/sign-up-login',
  '/homepage',
  '/faq',
  '/templates',
  '/template-detail',
  '/creators',
  '/auth',
];

function isPublicRoute(pathname: string): boolean {
  return PUBLIC_ROUTES.some((route) => pathname === route || pathname.startsWith(route + '/') || pathname.startsWith(route + '?'));
}

function HeaderWithDarkMode() {
  const { darkMode, toggleDarkMode } = useDarkMode();
  return <Header darkMode={darkMode} onToggleDark={toggleDarkMode} />;
}

function AppContent({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const isPublic = isPublicRoute(pathname);

  return (
    <>
      <HeaderWithDarkMode />
      {isPublic ? children : <AuthGuard>{children}</AuthGuard>}
      <PWAInstallPrompt />
    </>
  );
}

export default function LayoutProvider({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <DarkModeProvider>
          <NotificationProvider>
            <ToastProvider>
              <AppContent>{children}</AppContent>
            </ToastProvider>
          </NotificationProvider>
        </DarkModeProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}